/**
 * pages/Profile.tsx - User Profile Page
 * Displays and manages user profile information
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { usersAPI } from '@/lib/api/users';
import { verificationApi } from '@/services/verificationApi';
import { UserProfile } from '@/types/user';
import SEO from '@/components/common/SEO';
import AadhaarVerificationModal from '@/components/common/AadhaarVerificationModal';
import ProfileOnboarding from '@/components/common/ProfileOnboarding';
import { getAvatarUrl } from '@/lib/utils';
import {
  User,
  Mail,
  Phone,
  Calendar,
  Star,
  Shield,
  Edit2,
  Save,
  X,
  Loader2,
  AlertCircle,
  BadgeCheck,
  Camera,
} from 'lucide-react';

// Helper to format java LocalDateTime (array) or Instant (ISO string) safely for Profile
const formatProfileDate = (dateVal: any): string => {
  if (!dateVal) return 'N/A';
  
  let dateObj: Date;
  if (Array.isArray(dateVal)) {
    // LocalDateTime array: [year, month, day, hour, minute, second, nano]
    const [year, month, day, hour = 0, minute = 0, second = 0] = dateVal;
    // JS Date month is 0-indexed, Spring month is 1-indexed (1-12)
    dateObj = new Date(year, month - 1, day, hour, minute, second);
  } else {
    dateObj = new Date(dateVal);
  }

  if (isNaN(dateObj.getTime())) {
    return 'N/A';
  }

  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ProfilePage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationStatus, setVerificationStatus] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState<Record<string, any>>({});

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    bio: '',
  });

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Fetch user profile
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchProfile = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await usersAPI.getProfile();
        setProfile(data);
        setFormData({
          name: data.name,
          phone: data.phone || '',
          bio: data.bio || '',
        });
          setOnboarding({
            gender: data.gender,
            birthDate: data.birthDate,
            interests: data.interests,
            lookingFor: data.lookingFor,
            languages: data.languages,
            state: data.state,
            city: data.city,
          });
      } catch (err: any) {
        console.error('Error fetching profile:', err);
        setError(err?.message || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated]);

  const fetchVerificationStatus = async () => {
    if (!profile) return;
    try {
      const data = await verificationApi.getStatus(profile.id);
      if (data && 'status' in data) {
        setVerificationStatus((data as any).status);
      }
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchVerificationStatus();
  }, [profile?.id]);

  /**
   * Auto-crop image to a square focusing on the top-center (face area).
   * Produces a 512x512 JPEG for consistent, fast-loading profile photos.
   */
  const cropImageForProfile = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const TARGET = 512;
        canvas.width = TARGET;
        canvas.height = TARGET;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }

        const { naturalWidth: w, naturalHeight: h } = img;
        // Crop a square from the top-center of the image (where faces are)
        const cropSize = Math.min(w, h);
        const sx = (w - cropSize) / 2;  // center horizontally
        const sy = 0;                    // top-aligned to capture face

        ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, TARGET, TARGET);

        canvas.toBlob(
          (blob) => {
            if (!blob) { resolve(file); return; }
            const cropped = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            resolve(cropped);
          },
          'image/jpeg',
          0.9
        );
      };
      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !profile) return;
    
    // Check file size (e.g., 5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }
    
    setIsUploadingAvatar(true);
    setError(null);
    try {
      // Auto-crop to square focusing on face area before uploading
      const croppedFile = await cropImageForProfile(file);
      const response = await usersAPI.uploadAvatar(croppedFile);
      setProfile(prev => prev ? { ...prev, avatar: response.avatar } : prev);
    } catch (err: any) {
      console.error('Error uploading avatar:', err);
      setError(err?.message || 'Failed to upload profile photo');
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!profile) return;

    setIsSaving(true);
    setError(null);
    try {
      const updated = await usersAPI.updateProfile({
        ...formData,
        ...onboarding,
      });
      setProfile(prev => prev ? { ...prev, ...updated } : updated);
      setIsEditing(false);
    } catch (err: any) {
      console.error('Error updating profile:', err);
      setError(err?.message || 'Failed to update profile');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        name: profile.name,
        phone: profile.phone || '',
        bio: profile.bio || '',
      });
    }
    setIsEditing(false);
    setError(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">Loading profile...</span>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-6 w-6 text-destructive" />
              <div>
                <h3 className="font-semibold text-destructive">Failed to Load Profile</h3>
                <p className="text-sm text-destructive/80 mt-1">{error || 'Please try again later'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8 px-4 sm:px-6 lg:px-8">
      <SEO title="My Profile" noindex />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
            <User className="h-8 w-8 text-primary" />
            My Profile
          </h1>
          <p className="text-muted-foreground mt-2">Manage your account information</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {/* Profile Card */}
        <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
          {/* Avatar Section */}
          <div className="h-32 bg-gradient-to-r from-primary-500 to-primary-600"></div>

          <div className="px-6 sm:px-8 pb-6">
            {/* Profile Header */}
            <div className="flex items-start justify-between -mt-16 mb-6">
              <div className="flex items-end gap-4">
                <div 
                  className="w-24 h-24 rounded-xl border-4 border-card bg-secondary flex items-center justify-center relative group cursor-pointer overflow-hidden"
                  onClick={() => fileInputRef.current?.click()}
                >
                  {isUploadingAvatar ? (
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                  ) : profile.avatar ? (
                    <img 
                      src={getAvatarUrl(profile.avatar) || ''} 
                      alt={profile.name} 
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : (
                    <User className="h-12 w-12 text-muted-foreground" />
                  )}
                  {/* Fallback icon if image fails to load */}
                  {profile.avatar && (
                    <User className="h-12 w-12 text-muted-foreground hidden absolute inset-0 m-auto" />
                  )}
                  
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Camera className="h-6 w-6 text-white" />
                  </div>
                  <input 
                    type="file" 
                    ref={fileInputRef} 
                    onChange={handleAvatarChange}
                    accept="image/jpeg,image/png,image/webp" 
                    className="hidden" 
                  />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-foreground flex items-center gap-2">
                    {profile.name}
                    {verificationStatus === 'APPROVED' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" title="Aadhaar Verified">
                        <BadgeCheck className="h-3.5 w-3.5" />
                        Aadhaar Verified
                      </span>
                    )}
                    {verificationStatus === 'PENDING' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" title="Verification Pending">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Aadhaar Pending
                      </span>
                    )}
                    {verificationStatus === 'REJECTED' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200" title="Verification Rejected">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Aadhaar Rejected
                      </span>
                    )}
                    {(!verificationStatus || verificationStatus === 'NOT_SUBMITTED') && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-700 border border-slate-200" title="Not Verified">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Aadhaar Not Verified
                      </span>
                    )}
                  </h2>
                  <p className="text-muted-foreground flex items-center gap-1">
                    {profile.role === 'ADMIN' ? (
                      <>
                        <Shield className="h-4 w-4" />
                        Administrator
                      </>
                    ) : (
                      'Buyer & Seller'
                    )}
                  </p>
                </div>
              </div>
              {!isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-medium"
                >
                  <Edit2 className="h-4 w-4" />
                  Edit Profile
                </button>
              )}
            </div>

            {/* Profile Information */}
            <div className="space-y-4 mb-8">
              {/* Email */}
              <div className="flex items-start gap-4 pb-4 border-b border-border">
                <Mail className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Email (hide to others)</p>
                  <p className="text-foreground mt-1">{profile.email}</p>
                </div>
                {profile.isVerified && (
                  <div className="flex items-center gap-1 px-2 py-1 bg-success/10 rounded-lg">
                    <Shield className="h-4 w-4 text-success" />
                    <span className="text-xs font-semibold text-success">Verified</span>
                  </div>
                )}
              </div>

              {/* Edit Form - Phone */}
              {isEditing ? (
                <div className="flex items-start gap-4 pb-4 border-b border-border">
                  <Phone className="h-5 w-5 text-primary mt-2.5 shrink-0" />
                  <div className="flex-1">
                    <label htmlFor="phone" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                      Phone Number
                    </label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter your phone number"
                      className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4 pb-4 border-b border-border">
                  <Phone className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Phone (hide to others)</p>
                    <p className="text-foreground mt-1">{profile.phone || 'Not provided'}</p>
                  </div>
                </div>
              )}

              {/* Edit Form - Bio */}
              {isEditing ? (
                <div className="pb-4 border-b border-border">
                  <label htmlFor="bio" className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    placeholder="Tell us about yourself"
                    rows={4}
                    className="mt-2 w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20 resize-none"
                  />
                </div>
              ) : (
                <div className="pb-4 border-b border-border">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Bio</p>
                  <p className="text-foreground mt-1">{profile.bio || 'No bio provided'}</p>
                </div>
              )}

              {/* Community Onboarding (edit mode) */}
              {isEditing && (
                <div className="pb-4 border-b border-border">
                  <ProfileOnboarding data={onboarding} onChange={setOnboarding} />
                </div>
              )}

              {/* Friend Onboarding (view mode) */}
              {!isEditing && (onboarding.gender || onboarding.birthDate || onboarding.interests || onboarding.languages || onboarding.state) && (
                <div className="pb-4 border-b border-border space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Profile Details</p>
                  <div className="flex flex-wrap gap-2">
                    {onboarding.gender && <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-brand-soft text-primary border border-primary/20">{onboarding.gender}</span>}
                    {onboarding.birthDate && <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground border border-border">Born: {onboarding.birthDate}</span>}
                    {onboarding.lookingFor && <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground border border-border">Looking for: {onboarding.lookingFor}</span>}
                    {onboarding.interests && onboarding.interests.split(',').map((interest: string, i: number) => (
                      <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-xs bg-secondary text-secondary-foreground border border-border">{interest.trim()}</span>
                    ))}
                    {onboarding.languages && onboarding.languages.split(',').map((lang: string, i: number) => (
                      <span key={i} className="inline-flex px-2 py-0.5 rounded-full text-xs bg-accent text-accent-foreground border border-border">{lang.trim()}</span>
                    ))}
                    {onboarding.state && <span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground border border-border">{onboarding.city ? `${onboarding.city}, ${onboarding.state}` : onboarding.state}</span>}
                  </div>
                </div>
              )}

              {/* Ratings & Reviews */}
              <div className="flex items-start gap-4 pb-4 border-b border-border">
                <Star className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Rating</p>
                  <p className="text-foreground mt-1">
                    {(profile.ratings ?? 0.0).toFixed(1)} / 5 ({(profile.reviewCount ?? 0)} reviews)
                  </p>
                </div>
              </div>

              {/* Member Since */}
              <div className="flex items-start gap-4">
                <Calendar className="h-5 w-5 text-primary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Member Since</p>
                  <p className="text-foreground mt-1">
                    {formatProfileDate(profile.createdAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="flex gap-3 pt-6 border-t border-border">
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-border hover:bg-secondary transition font-medium text-foreground disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 transition font-medium disabled:opacity-50"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Additional Actions */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <a
            href="/listings"
            className="p-4 rounded-lg border border-border hover:bg-secondary transition text-center font-medium text-foreground"
          >
            View My Listings
          </a>
          <a
            href="/settings"
            className="p-4 rounded-lg border border-border hover:bg-secondary transition text-center font-medium text-foreground"
          >
            Go to Settings
          </a>
          <button
            onClick={() => setShowVerification(true)}
            className={`p-4 rounded-lg border transition text-center font-medium flex items-center justify-center gap-2 ${
              verificationStatus === 'PENDING' 
                ? 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 text-amber-600' 
                : 'border-primary/30 bg-primary/5 hover:bg-primary/10 text-foreground'
            }`}
          >
            <BadgeCheck className={`h-5 w-5 ${verificationStatus === 'PENDING' ? 'text-amber-500' : 'text-primary'}`} />
            {verificationStatus === 'PENDING' ? 'Verification Pending' : verificationStatus === 'APPROVED' ? 'Aadhaar Verified' : 'Verify Aadhaar'}
          </button>
        </div>
      </div>

      {/* Aadhaar Verification Modal */}
      <AadhaarVerificationModal
        userId={profile.id}
        open={showVerification}
        onClose={() => {
          setShowVerification(false);
          fetchVerificationStatus();
        }}
      />
    </div>
  );
};

export default ProfilePage;

